/**
 * hands.js —— 摄像头与 MediaPipe Hands
 *
 * 手部推理是整个应用最贵的一块，所以推理帧率和渲染帧率**分开**：
 * 渲染跑满 60，推理限到 22（tuning.GESTURE.inferenceFps），
 * 手势之间用平滑器插值。肉眼看不出差别，GPU 少掉三分之一负担。
 *
 * 这里只做「拿到 landmarks」这一件事，判定逻辑在 gestures.js。
 */

import { GESTURE } from './tuning.js';

// The tracker used to fetch its runtime from jsDelivr and its model from
// storage.googleapis.com. A phone could therefore grant camera permission
// while the recognizer itself silently remained unavailable. Keep every
// inference asset on the same origin as the application instead.
const localAssetUrl = (path) => new URL(path, document.baseURI).href;

function waitForVideoMetadata(video, timeoutMs = 10000) {
  if (video.readyState >= 1 && video.videoWidth > 0 && video.videoHeight > 0) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Camera metadata timed out before video dimensions became available'));
    }, timeoutMs);
    const check = () => {
      if (video.videoWidth <= 0 || video.videoHeight <= 0) return;
      cleanup();
      resolve();
    };
    const cleanup = () => {
      clearTimeout(timeout);
      video.removeEventListener('loadedmetadata', check);
      video.removeEventListener('loadeddata', check);
      video.removeEventListener('resize', check);
    };
    video.addEventListener('loadedmetadata', check);
    video.addEventListener('loadeddata', check);
    video.addEventListener('resize', check);
  });
}

/**
 * Create the requested task runner. A Safari GPU initialization can remain
 * pending forever without rejecting; only that condition triggers the one CPU
 * rebuild. A real GPU rejection is still surfaced instead of being hidden.
 */
export async function createHandLandmarkerWithFallback({
  create,
  requestedDelegate = 'CPU',
  timeoutMs = GESTURE.gpuCreateTimeoutMs,
  onStatus = () => {}
}) {
  if (requestedDelegate !== 'GPU') {
    return { landmarker: await create('CPU'), delegate: 'CPU', fallback: false };
  }

  let timeoutId;
  const gpuCreation = Promise.resolve().then(() => create('GPU'));
  const result = await Promise.race([
    gpuCreation.then((landmarker) => ({ kind: 'ready', landmarker })),
    new Promise((resolve) => {
      timeoutId = setTimeout(() => resolve({ kind: 'timeout' }), timeoutMs);
    })
  ]);
  clearTimeout(timeoutId);
  if (result.kind === 'ready') {
    return { landmarker: result.landmarker, delegate: 'GPU', fallback: false };
  }

  onStatus('gpu-timeout', { delegate: 'GPU', timeoutMs });
  // If the abandoned GPU promise eventually resolves, immediately release its
  // graph. It must never replace the CPU tracker that the user is already using.
  void gpuCreation.then((orphan) => orphan?.close?.()).catch(() => {});
  onStatus('delegate-fallback', { from: 'GPU', delegate: 'CPU' });
  return { landmarker: await create('CPU'), delegate: 'CPU', fallback: true };
}

export class HandTracker {
  constructor(o = {}) {
    this.video = o.video;
    this.onFrame = o.onFrame || (() => {});
    this.onStatus = o.onStatus || (() => {});
    this.requestedDelegate = o.delegate === 'GPU' ? 'GPU' : 'CPU';
    this.interval = 1 / (o.fps ?? GESTURE.inferenceFps);
    this._acc = 0;
    this.ready = false;
    this.landmarks = null;
    this.error = null;
    this.delegate = null;
    this._lastDetectTimestamp = -1;
    this._detectWindowStartedAt = 0;
    this._detectWindowCalls = 0;
    this.detectCallsPerSecond = 0;
    this.lastHandCount = 0;
  }

  async start() {
    try {
      this.onStatus('camera-requested');
      // Ask for and start the real camera first. Loading MediaPipe's WASM and
      // model can take several seconds on a cold cache; doing that first made
      // the OS camera indicator appear only much later and looked like the
      // camera had failed until another Jarvis interaction happened.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' }
      });
      // Set both DOM properties and attributes. iOS Safari has historically
      // treated these flags differently depending on how the video was made.
      this.video.playsInline = true;
      this.video.autoplay = true;
      this.video.muted = true;
      this.video.setAttribute('playsinline', '');
      this.video.setAttribute('webkit-playsinline', '');
      this.video.setAttribute('muted', '');
      this.video.setAttribute('autoplay', '');
      this.video.srcObject = stream;
      await this.video.play();
      await waitForVideoMetadata(this.video);
      this.onStatus('camera-ready');
      console.info('[camera:stream-ready]', JSON.stringify({
        width: this.video.videoWidth,
        height: this.video.videoHeight,
        readyState: this.video.readyState
      }));

      this.onStatus('model-loading');
      this.onStatus('vision-import-loading');
      const vision = await import('@mediapipe/tasks-vision');
      this.onStatus('vision-imported');
      this.onStatus('wasm-loading');
      const fileset = await vision.FilesetResolver.forVisionTasks(
        localAssetUrl('mediapipe/wasm')
      );
      this.onStatus('wasm-ready');
      const options = (delegate) => ({
        baseOptions: {
          modelAssetPath: localAssetUrl('models/hand_landmarker.task'),
          delegate
        },
        runningMode: 'VIDEO',
        numHands: 1,
        minHandDetectionConfidence: 0.5,
        minHandPresenceConfidence: 0.5,
        minTrackingConfidence: 0.5
      });
      // Delegate selection is explicit. In particular, do not silently fall
      // back from GPU: iOS Safari can initialize its GPU graph successfully
      // while returning empty inference results, which is not an exception.
      this.onStatus('delegate-selected', { delegate: this.requestedDelegate });
      const create = async (delegate) => {
        this.onStatus('landmarker-creating', { delegate });
        return vision.HandLandmarker.createFromOptions(fileset, options(delegate));
      };
      const created = await createHandLandmarkerWithFallback({
        create,
        requestedDelegate: this.requestedDelegate,
        onStatus: this.onStatus
      });
      this.landmarker = created.landmarker;
      this.delegate = created.delegate;
      this.fallback = created.fallback;
      console.log('[camera:delegate]', this.delegate);

      this.ready = true;
      this.onStatus('tracker-ready', { delegate: this.delegate, fallback: this.fallback });
      console.info('[camera:tracker-ready]', JSON.stringify({
        delegate: this.delegate,
        fallback: this.fallback
      }));
    } catch (e) {
      this.error = e;
      this.onStatus('error', { error: e });
      this.video?.srcObject?.getTracks?.().forEach((track) => track.stop());
      this.video.srcObject = null;
      throw e;
    }
  }

  /** 每帧调用；内部按 interval 限流 */
  update(dt, nowMs) {
    if (!this.ready) return;
    this._acc += dt;
    if (this._acc < this.interval) return;
    // 保留超出 interval 的余数；直接清零会把 60Hz 渲染下的 22Hz 推理
    // 系统性压到约 20Hz。
    this._acc -= this.interval;
    // MediaPipe silently drops frames with a repeated timestamp on iOS.
    const timestamp = Math.max(nowMs, this._lastDetectTimestamp + 1);
    this._lastDetectTimestamp = timestamp;
    const res = this.landmarker.detectForVideo(this.video, timestamp);
    if (this._detectWindowStartedAt === 0) this._detectWindowStartedAt = timestamp;
    this._detectWindowCalls += 1;
    const elapsed = timestamp - this._detectWindowStartedAt;
    if (elapsed >= 1000) {
      this.detectCallsPerSecond = this._detectWindowCalls * 1000 / elapsed;
      this._detectWindowCalls = 0;
      this._detectWindowStartedAt = timestamp;
    }
    if (!this._reportedFirstInference) {
      this._reportedFirstInference = true;
      console.info('[camera:first-inference-frame]', JSON.stringify({
        currentTime: this.video.currentTime,
        readyState: this.video.readyState
      }));
    }
    this.lastHandCount = Array.isArray(res.landmarks) ? res.landmarks.length : 0;
    this.landmarks = res.landmarks && res.landmarks[0] ? res.landmarks[0] : null;
    this.onFrame(this.landmarks);
  }

  stop() {
    this.video?.srcObject?.getTracks?.().forEach((t) => t.stop());
    this.landmarker?.close?.();
    this.ready = false;
    this._reportedFirstInference = false;
    this._lastDetectTimestamp = -1;
  }
}
